import {
  ColumnDef,
  flexRender,
  getCoreRowModel,
  useReactTable,
} from "@tanstack/react-table";
import { useState, useEffect } from "react";
import { Box } from "../Box";
import { Flex } from "../Flex";
import { Loader } from "../Loader";
import { useMandateStatuses } from "../../context/MandateStatusContext";
import "./table.css";
import { DateTime } from "luxon";
import { useTableMandates } from "../../data/useMandates";
import theme from "../../theme";
import { Mandate } from "../../types/Mandate";
import { PagedResponse } from "../../types/Common";
import { HiOutlineUser } from "react-icons/hi2";
import { StyledLink } from "../Breadcrumbs";
import { Paragraph } from "../Paragraph";
import { StatusBadge, statusToIntent } from "../StatusBadge";
import { SubjectTypeIcon } from "../SubjectTypeIcon";
import {
  useServerTable,
  TablePagination,
  SortIndicator,
  pageCountFor,
} from "../ServerTable";
// import { NoScopeSelectedNotice } from "../NoScopeSelectedNotice";

export const ApplicationsTable = (props) => {
  const {
    searchTerm,
    filterUserIds,
    filters,
    isBannerVisible,
    bannerHeight,
    isSmallBanner,
    hasFilterChips,
    _type,
    initialSorting,
    initialPageSize,
    onSortChange,
    onPageSizeChange,
  } = props;

  const { statuses, isLoadingStatuses } = useMandateStatuses();
  const [data, setData] = useState<Partial<PagedResponse<Mandate[]>>>({});
  const [filtersUpdate, setFiltersUpdate] = useState({ ...filters });
  const [searchTermUpdate, setSearchTermUpdate] = useState(props.searchTerm);
  const [filterUserIdsUpdate, setFilterUserIdsUpdate] = useState(
    props.filterUserIds
  );

  const cols: ColumnDef<Mandate>[] = [
    {
      header: () => "Name",
      accessorKey: "name",
      cell: (info) => {
        const { name, _type } = info.row.original;
        return (
          <Flex alignItems="center" gap="8">
            <SubjectTypeIcon isEntity={_type === "entity"} />
            <span
              style={{
                fontWeight: 600,
                fontSize: "15px",
                color: theme.colors.darkBlue,
              }}
            >
              {name}
            </span>
          </Flex>
        );
      },
    },
    {
      header: () => "Type",
      accessorKey: "mandate_type__name",
      cell: ({ row }) => {
        return (
          <span className="typeColumn">{row.original.mandate_type.name}</span>
        );
      },
    },
    {
      header: () => "Status",
      accessorKey: "status",
      cell: (info) => {
        const { status } = info.row.original;
        return (
          <StatusBadge intent={statusToIntent(getStatus(status))} withDot={false}>
            {getStatus(status)}
          </StatusBadge>
        );
      },
    },
    {
      header: () => "Division",
      accessorKey: "primary_division",
      cell: (info) => {
        return (
          <Flex color={theme.colors.gray[40]}>
            {info.row.original.primary_division?.display_name ?? "-"}
          </Flex>
        );
      },
    },
    {
      header: () => "Team",
      accessorKey: "primary_team",
      cell: (info) => {
        return (
          <Flex color={theme.colors.gray[40]}>
            {info.row.original.primary_team?.display_name ?? "-"}
          </Flex>
        );
      },
    },
    {
      header: () => "Individuals",
      accessorKey: "individual_count",
      cell: (info) => {
        return (
          <Flex justifyContent="start" alignItems="center" position="relative">
            <HiOutlineUser
              size={18}
              color={theme.colors.gray[60]}
              style={{ marginRight: "12px" }}
            />
            {info.row.original.individual_count}
          </Flex>
        );
      },
    },
    {
      header: () => "Reference",
      accessorKey: "reference",
      cell: (info) => {
        return (
          <Flex
            justifyContent="start"
            alignItems="center"
            position="relative"
            color={theme.colors.gray[40]}
          >
            {info.row.original.reference}
          </Flex>
        );
      },
    },
    {
      header: () => "Date started",
      accessorKey: "created_on",
      cell: (info) => {
        return (
          <Flex color={theme.colors.gray[40]}>
            {DateTime.fromISO(info.row.original.created_on).toFormat(
              "dd/MM/yyyy"
            )}
          </Flex>
        );
      },
    },
    {
      header: () => "",
      accessorKey: "assigned_user",
      cell: (info) => {
        const assignedUser = info.row.original.assigned_user;
        return (
          assignedUser &&
          (assignedUser?.first_name || assignedUser?.last_name) && (
            <Flex className="userCell">
              {assignedUser?.first_name?.charAt(0)?.toUpperCase()}
              {assignedUser?.last_name?.charAt(0)?.toUpperCase()}
            </Flex>
          )
        );
      },
    },
    {
      header: () => "",
      id: "view_id",
      accessorKey: "id",
      cell: (info) => {
        return (
          <StyledLink to={`${info.row.original.id}`}>
            <span
              style={{
                color: theme.colors.blue.primary,
              }}
            >
              View
            </span>
          </StyledLink>
        );
      },
    },
  ];

  const [columns] = useState([...cols]);
  const {
    pageIndex,
    pageSize,
    pagination,
    setPagination,
    sorting,
    setSorting,
    sortOrder,
    sortBy,
    resetPagination,
  } = useServerTable(initialSorting, initialPageSize, onSortChange, onPageSizeChange);

  const getMandatesData = useTableMandates(
    filterUserIdsUpdate,
    searchTermUpdate,
    filtersUpdate,
    pageIndex + 1,
    pageSize,
    sortOrder,
    sortBy,
    _type
  );

  useEffect(() => {
    resetPagination();
    // if (filters) {
    setFiltersUpdate(filters);
    // }
    // if (searchTerm) {
    setSearchTermUpdate(searchTerm);
    // }
    // if (filterUserIds) {
    setFilterUserIdsUpdate(filterUserIds);
    // }
  }, [searchTerm, searchTermUpdate, filterUserIds, filters, resetPagination]);

  useEffect(() => {
    setData(() => getMandatesData.data?.data);
  }, [getMandatesData]);

  const table = useReactTable({
    data: data?.results ?? [],
    columns,
    state: {
      pagination,
      sorting,
    },
    pageCount: pageCountFor(data?.count, pageSize),
    debugTable: true,
    onPaginationChange: setPagination,
    onSortingChange: setSorting,
    manualPagination: true,
    manualSorting: true,
    getCoreRowModel: getCoreRowModel(),
  });

  const getStatus = (statusId: number) => {
    if (statusId) {
      return statuses?.data?.find((s) => s.id === statusId).status;
    }
  };

  // Kept in step with the equivalent chip-row allowance in Applications.tsx
  // so the table's own scroll area shrinks to match, instead of overflowing
  // into the fixed-position pagination bar beneath it.
  const filterChipsHeight = hasFilterChips ? 56 : 0;

  const tableHeight = isBannerVisible
    ? `calc(100vh - ${bannerHeight > 0 ? bannerHeight : 260}px - ${
        searchTerm
          ? isSmallBanner
            ? "320px"
            : "400px"
          : isSmallBanner
          ? "270px"
          : "350px"
      } - ${filterChipsHeight}px)`
    : `calc(100vh - ${searchTerm ? "355px" : "300px"} - ${filterChipsHeight}px)`;

  return (
    <div>
      {/* Hidden for now — see NoScopeSelectedNotice. */}
      {/* <NoScopeSelectedNotice /> */}
      {/* One fixed-height area for loading, empty and loaded states, so the
          pagination below stays in the footer whichever one is showing. */}
      <Box height={tableHeight}>
        {(getMandatesData.isFetching || isLoadingStatuses) && (
          <Flex justifyContent="center" alignItems="center" height="100%">
            <Loader />
          </Flex>
        )}
        {!getMandatesData.isFetching &&
          !isLoadingStatuses &&
          data?.results?.length === 0 && (
            <Paragraph m={3} color="gray.40" textAlign="center">
              No Applications
            </Paragraph>
          )}

        {!getMandatesData.isFetching && data?.results?.length > 0 && (
          <div className="tableContainer" style={{ height: tableHeight }}>
            <table>
              <thead>
                {table.getHeaderGroups().map((headerGroup) => (
                  <tr key={headerGroup.id}>
                    {headerGroup.headers.map((header) => (
                      <th key={header.id} colSpan={header.colSpan}>
                        {header.isPlaceholder ? null : (
                          <>
                            <div
                              {...{
                                className: header.column.getCanSort()
                                  ? "select-none cursor-pointer flex items-center gap-1"
                                  : "",
                                onClick: header.column.getToggleSortingHandler(),
                              }}
                            >
                              {flexRender(
                                header.column.columnDef.header,
                                header.getContext()
                              )}
                              <SortIndicator column={header.column} />
                            </div>
                          </>
                        )}
                      </th>
                    ))}
                  </tr>
                ))}
              </thead>
              <tbody>
                {table.getRowModel().rows.map((row) => (
                  <tr key={row.id}>
                    {row.getVisibleCells().map((cell) => (
                      <td key={cell.id}>
                        {flexRender(
                          cell.column.columnDef.cell,
                          cell.getContext()
                        )}
                      </td>
                    ))}
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}
      </Box>
      <TablePagination table={table} />
    </div>
  );
};
